import { AppCopyText, AppPageHeader, AppSectionErrorBoundary, AppSpacer, AppUserDiagnosticsStateChip, AppUserDiagnosticsTagChip } from 'components/ui';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import { AppButton, AppRadioGroup } from 'components/form';
import { StaticTableWithFilters } from 'components/Tables/StaticTableWithFilters';
import { TableCell } from '@trackman/web-shared-components';
import { observer } from 'mobx-react-lite';
import { useStores } from 'index';
import { useTranslation } from 'react-i18next';
import { appTableFiltersInstance, userDiagnosticsTableColumns } from './UserDiagnosticsTableColumns';
import { IUserDiagnostic, UserDiagnosticsState, UserDiagnosticsTag } from 'types/models/UserDiagnosticsModel';
import CreateTicketReponseModal from './components/modals/CreateTicketReponseModal';
import { UserDiagnosticsManageDialog } from './components/dialogs/UserDiagnosticsManageDialog';
import { SingleSorterType } from 'services/AppTableFilters';
import { SimpleCreateTicketDialog } from './components/dialogs/SimpleCreateTicketDialog';

export const UserDiagnosticsPage = observer(() => {
    const { t } = useTranslation();

    const history = useHistory();
    const location = useLocation();

    const { userDiagnosticStore } = useStores();
    const [isControlDialogOpened, setIsControlDialogOpened] = useState<boolean>(false);
    const [isSimpleCreateTicketOpened, setIsSimpleCreateTicketOpened] = useState<boolean>(false);
    const [activeStateFilter, setActiveStateFilter] = useState<number>(0);

    const radioGroupOptions = [
        {
            label: t("UserDiagnostics.Table.RadioButton.AllState"),
            value: "0"
        },
        {
            label: t("UserDiagnostics.Table.RadioButton.NewState"),
            value: "1"
        },
        {
            label: t("UserDiagnostics.Table.RadioButton.ReviewedState"),
            value: "2"
        }
    ]

    const autoSortColumn = "addedAtUtc";
    const DefaultAutoSortColumn: SingleSorterType = {
        id: autoSortColumn,
        type: "date",
        field: autoSortColumn,
        value: 'desc',
        active: true
    };

    useEffect(() => {
        userDiagnosticStore.getUserDiagnostics();
        appTableFiltersInstance.clearSorters();
        appTableFiltersInstance.addSorter(DefaultAutoSortColumn);
    }, [userDiagnosticStore, userDiagnosticStore.page, userDiagnosticStore.take]);

    useEffect(() => {
        appTableFiltersInstance.clearSorters();
        appTableFiltersInstance.addSorter(DefaultAutoSortColumn);
    }, [activeStateFilter]);

    //Act based on SearchParams
    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        const selectedUserDiagnosticFromUrl = searchParams.get('selectedIssueReport');

        if (selectedUserDiagnosticFromUrl && !isControlDialogOpened) {
            const userDiagnostic = userDiagnosticStore.userDiagnostics.find(x => x.id == selectedUserDiagnosticFromUrl);
            if (userDiagnostic) {
                handleSelectingUserDiagnostic(userDiagnostic)
                userDiagnostic.state === UserDiagnosticsState.New &&
                    setActiveStateFilter(0)
            }
        }

        const organizationNameFilter = searchParams.get('organizationName');

        if (organizationNameFilter) {
            appTableFiltersInstance.addFilter({
                id: 'organizationName',
                type: 'textfields',
                label: `${t('organizationName')}: ${organizationNameFilter}`,
                field: 'organizationName',
                value: organizationNameFilter,
            });
        }
    }, [userDiagnosticStore.userDiagnostics]);

    useEffect(() => {
        if (!isControlDialogOpened && userDiagnosticStore.selectedUserDiagnostic) {
            userDiagnosticStore.setSelectedUserDiagnostic(null);
        }
    }, [isControlDialogOpened]);

    const handleSelectingRadioGroup = useCallback((value: string) => {
        setActiveStateFilter(parseInt(value))
    }, []);

    const allStateRows = useMemo(() => userDiagnosticStore.userDiagnostics, [userDiagnosticStore.userDiagnostics]);
    const newStateRows = useMemo(() => userDiagnosticStore.userDiagnostics.filter(x => x.state === UserDiagnosticsState.New), [userDiagnosticStore.userDiagnostics]);
    const reviewedStateRows = useMemo(() => userDiagnosticStore.userDiagnostics.filter(x => x.state !== UserDiagnosticsState.New), [userDiagnosticStore.userDiagnostics]);

    const stateFilterToList: Record<number, IUserDiagnostic[]> = {
        0: allStateRows,
        1: newStateRows,
        2: reviewedStateRows,
    }

    const columns = useMemo(() => userDiagnosticsTableColumns(), []);

    const renderCell = useCallback(
        ({ cellValue, columnId, row }: { cellValue: any; columnHeader: string; columnId: string; row: any }) => {
            if (columnId === 'state') {
                return <AppUserDiagnosticsStateChip userDiagnosticsState={cellValue as UserDiagnosticsState} />;
            }

            if (columnId === 'userDiagnosticId' || columnId === 'email') {
                return (
                    <div className='d-flex justify-content-between align-items-center w-100'>
                        <p data-test={`systemID-cell-text`} className='mb-0 overflow-ellipsis' title={cellValue}>
                            {cellValue}
                        </p>
                        <AppCopyText className='ml-1 ' text={cellValue} />
                    </div>
                );
            }

            if (columnId === 'tag' && Array.isArray(cellValue)) {
                const arrayInput = cellValue as UserDiagnosticsTag[];
                if (arrayInput !== null && arrayInput.length !== 0) {
                    return (
                        <div className='d-flex flex-column gap-10px align-items-center w-100'>
                            {arrayInput.map((x, index) => <AppUserDiagnosticsTagChip key={`tag-${index}`} userDiagnosticsTag={x as UserDiagnosticsTag} />)}
                        </div>
                    )
                }
                else {
                    return '-'
                }
            }

            if (columnId === 'addedAtUtc' || columnId === 'userSubject') {
                return (
                    <div className='d-flex justify-content-between align-items-center w-100'>
                        <p data-test={`systemID-cell-text`} className='mb-0 overflow-ellipsis' title={cellValue}>
                            {cellValue}
                        </p>
                    </div>
                );
            }

            if (columnId === 'actions') {
                const rowData: IUserDiagnostic = row as IUserDiagnostic;
                return (
                    <div className='d-flex justify-content-between align-items-center w-100'>
                        <AppButton
                            variant='secondary'
                            className='border-secondary'
                            onClick={(e) => {
                                e?.stopPropagation();
                                handleSelectingUserDiagnostic(rowData);
                            }}
                        >
                            {t('UserDiagnostics.Action.Manage')}
                        </AppButton>
                        <AppButton
                            variant='primary'
                            className='border-primary'
                            onClick={(e) => {
                                e?.stopPropagation();
                                handleOpenSimpleCreateTicket(rowData)
                            }}
                            disabled={rowData.state === UserDiagnosticsState.Tracked || rowData.state === UserDiagnosticsState.Closed}
                        >
                            {t('UserDiagnostics.Action.CreateTicket')}
                        </AppButton>
                    </div>
                );
            }

            return cellValue || '-';
        },
        []
    );

    const handleSelectingUserDiagnostic = useCallback((userDiagnostic: IUserDiagnostic) => {
        userDiagnosticStore.setSelectedUserDiagnostic(userDiagnostic);
        setIsControlDialogOpened(true);
        updateSelectedIssueReportUrlParams(userDiagnostic, true);
    }, []);
    const handleClosingControlDialog = useCallback(() => {
        setIsControlDialogOpened(false);
        updateSelectedIssueReportUrlParams(null, false);
    }, []);

    const handleOpenSimpleCreateTicket = useCallback((userDiagnostic: IUserDiagnostic) => {
        userDiagnosticStore.setSelectedUserDiagnostic(userDiagnostic);
        setIsSimpleCreateTicketOpened(true);
    }, []);

    const handleCloseSimpleCreateTicket = useCallback(() => {
        setIsSimpleCreateTicketOpened(false);
    }, []);

    const updateSelectedIssueReportUrlParams = useCallback((userDiagnostic: IUserDiagnostic | null, isApplyingParam: boolean) => {
        const searchParams = new URLSearchParams(location.search);
        if (isApplyingParam && userDiagnostic) {
            searchParams.delete('selectedIssueReport');
            searchParams.append('selectedIssueReport', userDiagnostic.id);
        }
        else {
            searchParams.delete('selectedIssueReport');
        }

        history.replace({
            pathname: location.pathname,
            search: searchParams.toString(),
        });
    }, []);

    const rowMapper = useCallback((cells: any[]) => {
        return (
            <>
                {cells.map(({ getCellProps, column, row, render, value }) => (
                    <TableCell {...getCellProps()} align={column.align} onClick={(e) => {
                        e?.stopPropagation();
                        handleSelectingUserDiagnostic(row.original as IUserDiagnostic);
                    }}>
                        {renderCell({ cellValue: value, columnHeader: column.Header, columnId: column.id, row: row.original })}
                    </TableCell>
                ))}
            </>
        );
    }, []);

    return (
        <AppSectionErrorBoundary>
            <div className='users-tables'>
                <AppPageHeader
                    title={`${t('UserDiagnostics.Title')}`}
                    backBtn={{ isVisible: true, onClick: () => history.push('/') }}
                >
                </AppPageHeader>

                <AppSpacer space='15px' />
                <AppRadioGroup value={activeStateFilter.toString()} options={radioGroupOptions} onChange={handleSelectingRadioGroup} isHorizontal={true} width='auto' />
                <AppSpacer space='15px' />

                <StaticTableWithFilters<IUserDiagnostic>
                    appTableFiltersInstance={appTableFiltersInstance}
                    rowMapper={rowMapper}
                    columns={columns}
                    data={stateFilterToList[activeStateFilter]}
                    isLoading={userDiagnosticStore.isLoadingUserDiagnostics}
                />

                <UserDiagnosticsManageDialog
                    isOpened={isControlDialogOpened}
                    onClose={handleClosingControlDialog}
                    userDiagnostic={userDiagnosticStore.selectedUserDiagnostic}
                    isDownloadingUserDiagnosticsFile={userDiagnosticStore.isDownloadingUserDiagnosticsFile}
                    isCreatingTicket={userDiagnosticStore.isCreatingTicket}
                />
                <SimpleCreateTicketDialog
                    isOpened={isSimpleCreateTicketOpened}
                    onClose={handleCloseSimpleCreateTicket}
                    isLoading={userDiagnosticStore.isLoadingUserDiagnostics}
                    isCreatingTicket={userDiagnosticStore.isCreatingTicket}
                    userDiagnostic={userDiagnosticStore.selectedUserDiagnostic}
                />
            </div>
            <CreateTicketReponseModal ticketUrl={userDiagnosticStore.currentTicketResponseUrl ?? '-'} onDialogClose={handleCloseSimpleCreateTicket} />
        </AppSectionErrorBoundary>
    );
});

export default UserDiagnosticsPage;
